import logger from '@eventbrite/client-logger';
import {
    createVenue,
    getCurrencySettings,
    LocationType,
    useLocalizationContext,
    useTimezone,
} from '@eventbrite/create-common';
import { track } from '@eventbrite/datalayer-library';
import { ACTION_EVENT_CREATED } from '@eventbrite/event-create-analytics';
import {
    CohortProps,
    saveSalesEligibleAnalytics,
} from '@eventbrite/sales-eligible-onboarding';
import { logEvent } from '@eventbrite/statsig';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
    AppContextState,
    ErrorSimulation,
    useAppContext,
} from '../../../AppContext';
import {
    AUTO_CREATE_EVENT_FORM_VALUES_KEY,
    TRANSIENT_AUTO_CREATED_EVENT_KEY,
    useSavedAutoCreatedEventFormValuesContext,
    useTransientAutoCreatedEventContext,
} from '../../../DraftEventStoreProvider';
import { APP_NAME } from '../../../shared';
import { ActionKeys, errorPropertyMap } from '../../../shared/TestingPanel';
import { getPublishError } from '../../Preview/publishErrors';
import { createEvent, createOrganization, getDefaultOrganizer } from '../api';
import { getOrganizations } from '../api/getOrganizations';
import { publishEvent } from '../api/publishEvent';
import { saveOnboardingResponses } from '../api/saveOnboardingResponses';
import { uploadImageFromUrl } from '../api/uploadImageFromUrl';
import { Organization } from '../types';
import {
    UseCreateOrganizationAndEventMutationResult,
    UseEventCreationProcessResult,
} from './types';

/**
 * Important note: The context values read in the callback functions (e.g onCreateEventSuccess) are delayed by one render cycle.
 */

/**
 * Custom hook for creating an organization mutation.
 *
 * @param {Object} options - The options for the mutation.
 * @param {Function} options.onSuccess - The callback function to be called on successful mutation.
 * @param {Function} options.onError - The callback function to be called on error during mutation.
 * @param {Function} options.onSettled - The callback function to be called after mutation is settled.
 * @returns {Object} - The mutation object.
 */
export const useCreateOrganizationMutation = ({
    onSuccess,
    onError,
    onSettled,
}: {
    onSuccess?: (data: Organization) => void;
    onError?: (error: Error) => void;
    onSettled?: () => void;
} = {}) => {
    const { locale } = useLocalizationContext();
    const {
        errorSimulation: { shouldErrorOnCreateOrganization },
    } = useAppContext();

    return useMutation({
        mutationFn: ({ name, publicId }: { publicId: string; name: string }) =>
            createOrganization({
                name,
                publicId,
                locale,
                options: {
                    testingFlags: {
                        shouldError: shouldErrorOnCreateOrganization,
                    },
                },
            }),
        onSuccess,
        onError,
        onSettled,
    });
};

/**
 * Custom hook for creating an event mutation.
 *
 * @param {Object} options - The options for the mutation.
 * @param {Function} options.onSuccess - The callback function to be called on successful mutation.
 * @param {Function} options.onError - The callback function to be called on error during mutation.
 * @param {Function} options.onSettled - The callback function to be called after mutation is settled.
 * @returns {Object} - The mutation object.
 */
export const useCreateEventMutation = ({
    onSuccess,
    onError,
    onSettled,
}: {
    onSuccess?: (data: any) => void;
    onError?: (error: Error) => void;
    onSettled?: () => void;
} = {}) => {
    const {
        locale,
        timezone: userTimezone,
        currency: userCurrency,
    } = useLocalizationContext();
    const { savedAutoCreateFormValues } =
        useSavedAutoCreatedEventFormValuesContext();
    const {
        errorSimulation: { shouldErrorOnCreateEvent },
    } = useAppContext();
    const { transientAutoCreatedEvent } = useTransientAutoCreatedEventContext();

    const { fetch: fetchTimezone } = useTimezone();

    return useMutation({
        mutationFn: async ({
            organizationId,
            organizerId,
        }: {
            organizationId: string;
            organizerId: string;
        }) => {
            let venue;
            let timezone = userTimezone;
            let currency = userCurrency;
            let venuePromise;
            let imagePromise;

            if (
                savedAutoCreateFormValues?.locationType ===
                    LocationType.Venue &&
                savedAutoCreateFormValues.venue?.id
            ) {
                venuePromise = Promise.all([
                    createVenue({
                        organizationId,
                        values: {
                            ...savedAutoCreateFormValues,
                            venue: savedAutoCreateFormValues.venue,
                        },
                    }),
                    fetchTimezone({
                        latitude: savedAutoCreateFormValues.venue.latitude,
                        longitude: savedAutoCreateFormValues.venue.longitude,
                    }),
                    getCurrencySettings(),
                ]);
            }

            if (transientAutoCreatedEvent?.image_url) {
                imagePromise = uploadImageFromUrl(
                    transientAutoCreatedEvent.image_url,
                );
            }

            const [venueResults, imageResults] = await Promise.all([
                venuePromise,
                imagePromise,
            ]);

            if (venueResults) {
                const [createdVenue, venueTimezone, currencySettings] =
                    venueResults;

                venue = createdVenue;
                currency =
                    currencySettings?.default_currencies_by_country[
                        venue.address.country
                    ] || currency;
                timezone = venueTimezone || timezone;
            }

            const image = imageResults;

            return createEvent({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values: savedAutoCreateFormValues!,
                currency,
                image,
                locale,
                organizationId,
                organizerId,
                options: {
                    testingFlags: { shouldError: shouldErrorOnCreateEvent },
                },
                timezone,
                transientEvent: transientAutoCreatedEvent,
                venue,
            });
        },
        onError,
        onSuccess,
        onSettled,
    });
};

/**
 * Custom hook for creating a publish mutation.
 *
 * @param {Object} options - The options for the mutation.
 * @param {Function} options.onSuccess - The callback function to be called on successful mutation.
 * @param {Function} options.onError - The callback function to be called on error during mutation.
 * @param {Function} options.onSettled - The callback function to be called after mutation is settled.
 * @returns {Object} - The mutation object.
 */
const usePublishEventMutation = ({
    onSuccess,
    onError,
    onSettled,
}: {
    onSuccess?: (data: any) => void;
    onError?: (error: Error) => void;
    onSettled?: () => void;
} = {}) => {
    const {
        errorSimulation: { shouldErrorOnPublishEvent },
    } = useAppContext();

    return useMutation({
        mutationFn: ({ eventId }: { eventId: string }) =>
            publishEvent({
                eventId,
                options: {
                    testingFlags: { shouldError: shouldErrorOnPublishEvent },
                },
            }),
        onError,
        onSuccess,
        onSettled,
    });
};

/**
 * Custom hook to publish an event.
 *
 * @returns {Function} - The function to publish an event.
 */
export const usePublishEvent = () => {
    const { createdEventId, errorSimulation, updateContext } = useAppContext();

    const {
        shouldErrorOnPublishEvent,
        publishEventErrorThreshold,
        publishEventErrorCount,
    } = errorSimulation;

    const onPublishEventSuccess = () => {
        sessionStorage.removeItem(TRANSIENT_AUTO_CREATED_EVENT_KEY);
        sessionStorage.removeItem(AUTO_CREATE_EVENT_FORM_VALUES_KEY);
        window.location.assign(`/myevent/${createdEventId}/invite-and-promote`);
    };

    const onPublishEventError = (error: any) => {
        const contextUpdates: Partial<AppContextState> = {
            didPublishEventRequestFail: true,
            isPublishingEvent: false,
        };

        if (shouldErrorOnPublishEvent) {
            let shouldError = true;

            if (publishEventErrorCount + 1 >= publishEventErrorThreshold) {
                shouldError = false;
            }

            contextUpdates.errorSimulation = {
                ...errorSimulation,
                publishEventErrorCount: publishEventErrorCount + 1,
                shouldErrorOnPublishEvent: shouldError,
            };
        }

        const publishError = getPublishError(error?.parsedError);

        if (publishError) {
            contextUpdates.publishError = publishError;
        }

        updateContext(contextUpdates);
    };

    const { mutate } = usePublishEventMutation({
        onSuccess: onPublishEventSuccess,
        onError: onPublishEventError,
    });

    const publishEvent = () => {
        updateContext({
            isPublishingEvent: true,
            dismissedRetrySuccessfulAlert: true,
        });
        createdEventId && mutate({ eventId: createdEventId });
    };

    return publishEvent;
};

/**
 * Custom hook for saving onboarding responses mutation.
 *
 * @param {Object} options - The options for the mutation.
 * @param {Function} options.onSuccess - The callback function to be called on successful mutation.
 * @param {Function} options.onError - The callback function to be called on error during mutation.
 * @param {Function} options.onSettled - The callback function to be called after mutation is settled.
 * @returns {Object} - The mutation object.
 */
export const useSaveOnboardingResponsesMutation = ({
    onSuccess,
    onError,
    onSettled,
}: {
    onSuccess?: (data: any) => void;
    onError?: (error: Error) => void;
    onSettled?: () => void;
} = {}) => {
    const {
        errorSimulation: { shouldErrorOnSaveOnboardingResponses },
    } = useAppContext();

    return useMutation({
        mutationFn: ({
            organizationId,
            publicId,
        }: {
            organizationId: string;
            publicId: string;
        }) =>
            saveOnboardingResponses({
                organizationId,
                publicId,
                options: {
                    testingFlags: {
                        shouldError: shouldErrorOnSaveOnboardingResponses,
                    },
                },
            }),
        onError,
        onSuccess: (_, variables) =>
            onSuccess && onSuccess(variables?.organizationId),
        onSettled,
    });
};

/**
 * Custom hook that combines the functionality of creating an organization and creating an event.
 * This hook uses the `useCreateOrganizationMutation` and `useCreateEventMutation` hooks internally.
 * It handles the success and failure callbacks for both mutations and provides a convenient way to create an organization and event together.
 *
 * @returns {useCreateOrganizationMutationResult} The result of the `useCreateOrganizationMutation` hook, which includes the mutation function and other useful properties.
 */
const useCreateOrganizationAndEventMutation =
    (): UseCreateOrganizationAndEventMutationResult => {
        const {
            errorSimulation,
            retryCount,
            updateContext,
            user,
            phoneNumber,
            shouldSendSalesEligibleEvent,
        } = useAppContext();
        const { saveTransientAutoCreatedEvent } =
            useTransientAutoCreatedEventContext();

        const createEventWithOrganizationId = async (
            organizationId: string,
        ) => {
            try {
                const { id: organizerId } =
                    (await getDefaultOrganizer(organizationId)) || {};

                if (!organizerId) {
                    // dispatch error to retry event creation with the organization id
                    updateContext({
                        didGetOrganizerIdRequestFail: true,
                        isPerformingAsyncAction: false,
                        isSubmittingCreateAccountForm: false,
                        isSocialSignUpLoading: false,
                    });
                    return;
                }

                sessionStorage.removeItem('activeOrganizationId');

                updateContext({
                    didGetOrganizerIdRequestFail: false,
                    _organizerId: organizerId,
                });

                createEventMutation.mutate({
                    organizationId,
                    organizerId,
                });
            } catch (error) {
                // dispatch error to retry event creation with the organization id
                updateContext({
                    didGetOrganizerIdRequestFail: true,
                    isPerformingAsyncAction: false,
                    isSubmittingCreateAccountForm: false,
                    isSocialSignUpLoading: false,
                });
            }
        };

        const sendDataToSalesEligible = async (
            publicId: string | null,
            activeOrganizationId: string,
        ) => {
            const { email, firstName, lastName } = user || {};
            if (!email || !firstName || !lastName || !phoneNumber) return;

            saveSalesEligibleAnalytics({
                userId: publicId || activeOrganizationId,
                phoneNumber,
                email,
                firstName,
                lastName,
                cohort: CohortProps.NewBusiness,
                activeOrgId: activeOrganizationId,
            });
        };

        // onSuccess callbacks
        const onCreateOrganizationSuccess = async (data: Organization) => {
            updateContext({
                hasOrganization: true,
                didCreateOrganizationRequestFail: false,
                _activeOrganizationId: data.id,
            });

            sessionStorage.setItem('activeOrganizationId', data.id);
            const publicId = sessionStorage.getItem('publicId');

            if (shouldSendSalesEligibleEvent) {
                sendDataToSalesEligible(publicId, data.id);
            }

            saveOnboardingResponsesMutation.mutate({
                organizationId: data.id,
                publicId: publicId || '',
            });
        };

        const onCreateEventSuccess = (data: any) => {
            // save the event id in session storage for use when page is refreshed/navigated away
            saveTransientAutoCreatedEvent({ id: data?.event?.id });

            track({
                eventName: ACTION_EVENT_CREATED,
                eventData: {
                    eventId: data?.event?.id,
                    eventCreateSource: APP_NAME,
                    isPaid: Boolean(data?.ticket_class?.free) == false,
                },
            });

            logEvent(ACTION_EVENT_CREATED, null, {
                eventId: data?.event?.id,
                eventCreateSource: APP_NAME,
                isPaid: (Boolean(data?.ticket_class?.free) == false).toString(),
            });

            updateContext({
                didCreateEvent: true,
                didCreateEventRequestFail: false,
                createdEventId: data?.event?.id,
                isModalShowing: retryCount == 0,
            });
        };

        const onSaveOnboardingResponsesSuccess = async (
            organizationId: string,
        ) => {
            updateContext({ didSaveOnboardingResponsesRequestFail: false });
            sessionStorage.removeItem('publicId');
            await createEventWithOrganizationId(organizationId || '');
        };

        // onError callbacks
        const onCreateEventFailure = (error: Error) => {
            handleAsyncActionFailure({
                error,
                action: 'create event',
                errorSimulation,
                updateContext,
            });
        };

        const onCreateOrganizationFailure = (error: Error) => {
            handleAsyncActionFailure({
                error,
                action: 'create organization',
                errorSimulation,
                updateContext,
            });
        };

        const onSaveOnboardingResponsesFailure = (error: any) => {
            handleAsyncActionFailure({
                error,
                action: 'save onboarding responses',
                errorSimulation,
                updateContext,
            });
        };

        // to reset the disabled state of the create account form while async operations are in progress
        const onCreateEventSettled = () => {
            updateContext({
                isSubmittingCreateAccountForm: false,
                isPerformingAsyncAction: false,
                isLoadingInInnerComponent: false,
                isSocialSignUpLoading: false,
            });
        };

        // mutations
        const createEventMutation = useCreateEventMutation({
            onSuccess: onCreateEventSuccess,
            onError: onCreateEventFailure,
            onSettled: onCreateEventSettled,
        });

        const createOrganizationMutation = useCreateOrganizationMutation({
            onSuccess: onCreateOrganizationSuccess,
            onError: onCreateOrganizationFailure,
        });

        const saveOnboardingResponsesMutation =
            useSaveOnboardingResponsesMutation({
                onSuccess: onSaveOnboardingResponsesSuccess,
                onError: onSaveOnboardingResponsesFailure,
            });

        return {
            createEventMutation,
            createEventWithOrganizationId,
            createOrganizationAndEventMutation: createOrganizationMutation,
            isCreateOrganizationAndEventMutationLoading:
                createOrganizationMutation.isLoading ||
                createEventMutation.isLoading ||
                saveOnboardingResponsesMutation.isLoading,
            saveOnboardingResponsesMutation,
            onCreateOrganizationSuccess,
        };
    };

/**
 * Custom hook that encapsulates the event creation process.
 * This hook uses the `useCreateOrganizationAndEventMutation` hook internally.
 * It provides a way to retry the event creation process if it fails.
 *
 * @returns {UseEventCreationProcessResult} The result of the `useEventCreationProcess` hook, which includes the mutation functions and other useful properties.
 */
export const useEventCreationProcess = (): UseEventCreationProcessResult => {
    const {
        _publicId,
        _activeOrganizationId,
        _organizerId,
        retryCount,
        didSaveOnboardingResponsesRequestFail,
        updateContext,
        user,
    } = useAppContext();

    const {
        createEventMutation,
        createEventWithOrganizationId,
        createOrganizationAndEventMutation,
        isCreateOrganizationAndEventMutationLoading,
        saveOnboardingResponsesMutation,
        onCreateOrganizationSuccess,
    } = useCreateOrganizationAndEventMutation();

    const organizationName = user?.fullName || 'My Organization';

    /**
     * Retries the event creation process.
     * If the organization creation failed, it retries creating an organization and then the event.
     * If the event creation failed, it retries just creating the event.
     */
    const retryEventCreationProcess = useCallback(async (): Promise<void> => {
        updateContext({
            didRetry: true,
            retryCount: retryCount + 1,
            isPerformingAsyncAction: true,
        });

        if (didSaveOnboardingResponsesRequestFail) {
            saveOnboardingResponsesMutation.mutate({
                organizationId: _activeOrganizationId || '',
                publicId: _publicId || '',
            });
        } else if (_publicId && organizationName && !_activeOrganizationId) {
            const userOrganizations = await getOrganizations({
                publicId: _publicId,
            });

            const organization = userOrganizations?.organizations?.[0];

            if (organization) {
                await onCreateOrganizationSuccess(organization);
            } else {
                // organization creation failed, retrying that
                createOrganizationAndEventMutation.mutate({
                    publicId: _publicId,
                    name: organizationName,
                });
            }
        } else if (_activeOrganizationId && _organizerId) {
            // event creation failed, let's retry creating it
            createEventMutation.mutate({
                organizationId: _activeOrganizationId,
                organizerId: _organizerId,
            });
        } else if (_activeOrganizationId) {
            // event creation failed, let's retry creating it
            createEventWithOrganizationId(_activeOrganizationId);
        }
    }, [
        _publicId,
        _activeOrganizationId,
        organizationName,
        _organizerId,
        createOrganizationAndEventMutation,
        createEventMutation,
        didSaveOnboardingResponsesRequestFail,
        saveOnboardingResponsesMutation,
        retryCount,
        createEventWithOrganizationId,
        updateContext,
        onCreateOrganizationSuccess,
    ]);

    return {
        createOrganizationAndEventMutation,
        createEventWithOrganizationId,
        createEventMutation,
        isCreatingOrganizationAndOrEvent:
            isCreateOrganizationAndEventMutationLoading ||
            createEventMutation.isLoading,
        retryEventCreationProcess,
        onCreateOrganizationSuccess,
        saveOnboardingResponsesMutation,
    };
};

export const handleAsyncActionFailure = ({
    error,
    action,
    errorSimulation,
    updateContext,
}: {
    error: any;
    action: ActionKeys;
    errorSimulation: ErrorSimulation;
    updateContext: (newState: Partial<AppContextState>) => void;
}) => {
    const {
        shouldError: shouldErrorKey,
        errorCount: errorCountKey,
        threshold: thresholdKey,
        didError: didErrorKey,
    } = errorPropertyMap[action];

    const contextUpdates: Partial<AppContextState> = {
        [didErrorKey]: true,
        isPerformingAsyncAction: false,
        isSubmittingCreateAccountForm: false,
        isSocialSignUpLoading: false,
    };

    const errorCount = (errorSimulation as Record<string, any>)[errorCountKey];
    const errorThreshold = (errorSimulation as Record<string, any>)[
        thresholdKey
    ];

    if (errorSimulation[shouldErrorKey]) {
        let shouldError = true;

        if (errorCount + 1 >= errorThreshold) {
            shouldError = false;
        }

        contextUpdates.errorSimulation = {
            ...errorSimulation,
            [errorCountKey]: errorCount + 1,
            [shouldErrorKey]: shouldError,
        };
    }

    console.log(error);
    logger.error(error, { message: `Error [${action}] in ${APP_NAME}` });
    updateContext(contextUpdates);
};
